import { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const TenantContext = createContext(null);

// Valider qu'une couleur est bien un format hex sécurisé
const isValidHexColor = (color) => /^#[0-9A-Fa-f]{3,8}$/.test(color);
const safeSetColor = (varName, color) => {
  if (color && isValidHexColor(color)) {
    document.documentElement.style.setProperty(varName, color);
  }
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
};

// Déterminer le tenant depuis le domaine
const detectTenantFromDomain = () => {
  const hostname = window.location.hostname;
  
  if (hostname.includes('cloudyva')) return 'cloudyva';
  
  // En dev, utiliser un paramètre URL ou localStorage
  const urlParams = new URLSearchParams(window.location.search);
  const tenantParam = urlParams.get('tenant');
  if (tenantParam) return tenantParam;
  
  return localStorage.getItem('devTenant') || 'cloudyva';
};

export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger les infos du tenant
  useEffect(() => {
    const loadTenant = async () => {
      const slug = detectTenantFromDomain();
      
      try {
        const response = await api.get(`/organizations/branding/${slug}`);
        const tenantData = response.data;
        
        setTenant(tenantData);
        
        // Appliquer le thème
        document.documentElement.setAttribute('data-theme', tenantData.slug);
        document.title = `Support IT - ${tenantData.name}`;
        
        // Mettre à jour les CSS variables si couleurs personnalisées
        safeSetColor('--color-primary', tenantData.primaryColor);
        safeSetColor('--color-secondary', tenantData.secondaryColor);
        
      } catch (err) {
        console.error('Erreur chargement tenant:', err);
        setError('Organisation non trouvée');
      } finally {
        setLoading(false);
      }
    };

    loadTenant();
  }, []);

  // Fonction pour changer de tenant (dev/admin)
  const switchTenant = async (slug) => {
    setLoading(true);
    
    try {
      const response = await api.get(`/organizations/branding/${slug}`);
      const tenantData = response.data;
      
      setTenant(tenantData);
      localStorage.setItem('devTenant', slug);
      
      // Appliquer le nouveau thème
      document.documentElement.setAttribute('data-theme', tenantData.slug);
      document.title = `Support IT - ${tenantData.name}`;
      
      if (tenantData.primaryColor) {
        document.documentElement.style.setProperty('--color-primary', tenantData.primaryColor);
      }
      if (tenantData.secondaryColor) {
        document.documentElement.style.setProperty('--color-secondary', tenantData.secondaryColor);
      }
      
    } catch (err) {
      console.error('Erreur switch tenant:', err);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    tenant,
    loading,
    error,
    switchTenant,
    isCloudyva: tenant?.slug === 'cloudyva'
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
